import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, TrendingUp, Flame, AlertTriangle, RefreshCw, ArrowUpRight, Users } from "lucide-react";

const KIND_META = {
  recoverable_revenue: { Icon: RefreshCw, color: "from-emerald-400 to-teal-600", tag: "Recoverable" },
  hot_leads: { Icon: Flame, color: "from-orange-400 to-rose-500", tag: "Hot leads" },
  upsell: { Icon: ArrowUpRight, color: "from-violet-500 to-fuchsia-500", tag: "Upsell" },
  churn_risk: { Icon: AlertTriangle, color: "from-rose-500 to-red-600", tag: "Churn risk" },
  inactive_buyers: { Icon: Users, color: "from-amber-400 to-orange-500", tag: "Reactivate" },
  top_funnel: { Icon: TrendingUp, color: "from-violet-500 to-indigo-600", tag: "Scale" },
};

export default function Opportunities() {
  const [d, setD] = useState(null);

  const load = () => api.get("/opportunities/center").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="p-10 text-zinc-400">Scanning your workspace for money-finding opportunities…</div>;

  const s = d.summary;
  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="opportunities-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-violet-600 grid place-items-center">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">AI Opportunity Center</h1>
            <p className="text-sm text-zinc-500 mt-1">Money your AI is finding for you — automatically.</p>
          </div>
        </div>
        <button onClick={load} className="text-xs px-3 py-2 rounded-full bg-white border border-black/10 hover:bg-zinc-50 inline-flex items-center gap-1" data-testid="opp-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Re-scan
        </button>
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-500 to-pink-500 p-[1px] mb-6">
        <div className="rounded-3xl bg-white p-7">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Total opportunity value</p>
          <p className="font-display text-5xl font-extrabold mt-1 bg-gradient-to-r from-violet-600 to-pink-500 bg-clip-text text-transparent" data-testid="total-opportunity">
            ${Math.round(s.total_opportunity_value).toLocaleString()}
          </p>
          <p className="text-sm text-zinc-500 mt-2">AI is currently working on {d.opportunities.length} active opportunities.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Recoverable revenue" value={`$${Math.round(s.recoverable_revenue).toLocaleString()}`} tone="emerald" />
        <Stat label="Hot leads waiting" value={`${s.hot_leads_count} · $${Math.round(s.hot_leads_revenue).toLocaleString()}`} tone="rose" />
        <Stat label="Upsell potential" value={`$${Math.round(s.upsell_potential).toLocaleString()}`} tone="violet" />
        <Stat label="Churn risks" value={s.churn_risks} tone="amber" />
      </div>

      <h2 className="font-display text-lg font-bold mb-3">AI-prioritized opportunities</h2>
      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        {d.opportunities.map((o, i) => {
          const meta = KIND_META[o.kind] || KIND_META.upsell;
          const Icon = meta.Icon;
          return (
            <div key={i} className="rounded-3xl bg-white border border-black/5 p-6 hover:shadow-md transition" data-testid={`opp-card-${o.kind}`}>
              <div className="flex items-start gap-4">
                <div className={`h-11 w-11 rounded-2xl bg-gradient-to-br ${meta.color} grid place-items-center shrink-0`}>
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{meta.tag}</span>
                  </div>
                  <p className="font-display text-lg font-bold">{o.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{o.detail}</p>
                  <div className="flex items-end justify-between mt-3">
                    <p className="font-display text-2xl font-extrabold text-zinc-900">
                      {typeof o.value === "number" && o.kind !== "top_funnel" && o.kind !== "churn_risk" && o.kind !== "inactive_buyers"
                        ? `$${Math.round(o.value).toLocaleString()}`
                        : (o.kind === "top_funnel" ? `${Math.round(o.value)}%` : o.value)}
                    </p>
                    <button className="text-xs font-medium text-violet-600 hover:underline">{o.action} →</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-3">Hot leads (war room)</h3>
          <ul className="space-y-2">
            {d.hot_leads.map((l) => (
              <li key={l.id} className="flex items-center justify-between text-sm py-2 border-b border-black/5 last:border-none">
                <div>
                  <p className="font-medium">{l.name}</p>
                  <p className="text-xs text-zinc-500">{l.handle} · {l.source}</p>
                </div>
                <div className="text-right">
                  <p className="font-display font-bold text-zinc-900">${Math.round(l.revenue_potential).toLocaleString()}</p>
                  <p className="text-[11px] text-zinc-500">score {l.score}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-3">Top-converting funnels</h3>
          <ul className="space-y-3">
            {d.top_funnels.map((f) => (
              <li key={f.id} className="rounded-2xl bg-zinc-50 p-4">
                <div className="flex items-center justify-between">
                  <p className="font-medium">{f.name}</p>
                  <span className="text-emerald-600 text-sm font-bold">{Math.round(f.conversion * 100)}%</span>
                </div>
                <p className="text-xs text-zinc-500 mt-1">${Math.round(f.revenue_30d).toLocaleString()} (30d)</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = { emerald: "text-emerald-600", rose: "text-rose-600", violet: "text-violet-600", amber: "text-amber-600" }[tone] || "text-zinc-900";
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${toneCls}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>{value}</p>
    </div>
  );
}
